import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { User, Lock, Phone, Eye, EyeOff, Shield, ArrowRight, Heart, Users } from "lucide-react"
import { motion } from "framer-motion"
import { useAuth } from '../../context/AuthContext'  // Updated path to go up two levels
import { toast } from 'react-hot-toast'
import SEOHead from '../SEO/SEOHead'

const Login = () => {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  })
  const [selectedRole, setSelectedRole] = useState('donor') // Default to donor
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')

    try {
      // Call the login function from AuthContext with email, password, and rememberMe
      await login(formData.email, formData.password, formData.rememberMe, selectedRole)
      // Navigation is handled in the AuthContext after successful login
    } catch (error) {
      console.error('Login error:', error)
      const errorMessage = error.response?.data?.message || 'Invalid credentials. Please try again.'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <SEOHead
        title="Blood Donor Login | NGO Login | BloodHero Blood Donation Platform"
        description="Login to BloodHero blood donation platform as a blood donor, NGO, hospital, or vehicle owner. Access your dashboard to manage blood donation requests, emergency blood services, and save lives across India."
        keywords="blood donor login, NGO login, hospital login, vehicle owner login, blood donation platform login, BloodHero login, blood donation dashboard, emergency blood service login"
        canonicalUrl="/login"
        ogImage="/images/blood-hero-login-og.jpg"
        ogType="website"
      />

      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center pt-20 sm:pt-24 pb-8 sm:pb-12 px-4 relative overflow-hidden">
        {/* Background Animation */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{
                y: [0, -20, 0],
                opacity: [0.3, 0.6, 0.3],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 5 + i,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.5
              }}
              className="absolute rounded-full bg-red-500 blur-2xl opacity-10"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${200 + i * 50}px`,
                height: `${200 + i * 50}px`,
              }}
            />
          ))}
          {/* Floating Blood Cells */}
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={`cell-${i}`}
              initial={{ y: "120vh" }}
              animate={{
                y: "-20vh",
                rotate: 360,
              }}
              transition={{
                duration: 15 + Math.random() * 10,
                repeat: Infinity,
                ease: "linear",
                delay: Math.random() * 20
              }}
              className="absolute w-12 h-12 opacity-20"
              style={{
                left: `${Math.random() * 100}%`,
              }}
            >
              <div className="w-full h-full bg-red-500 rounded-full opacity-60" />
              <div className="absolute top-2 left-2 w-3 h-3 bg-white rounded-full opacity-40" />
            </motion.div>
          ))}
        </div>
        <div className="w-full max-w-md mx-auto relative z-10">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200 p-5 sm:p-6"
          >
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Heart className="w-6 h-6 text-red-600" />
              </div>
              <h1 className="text-xl font-bold text-gray-900 mb-1">Sign in to BloodHero</h1>
              <p className="text-gray-600 text-sm">Access your blood donation dashboard and save lives</p>
            </div>

            {/* Role Selection */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-700 mb-2 text-center">
                Login as
              </label>
              <div className="grid grid-cols-2 gap-2">
                <motion.button
                  type="button"
                  onClick={() => setSelectedRole('donor')}
                  className={`relative p-2 rounded-lg border transition-all duration-300 ${selectedRole === 'donor'
                    ? 'border-red-500 bg-red-50 text-red-700 shadow-sm'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex flex-col items-center space-y-1">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${selectedRole === 'donor'
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-100 text-gray-500'
                      }`}>
                      <Heart className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-xs font-semibold">Donor</span>
                  </div>
                  {selectedRole === 'donor' && (
                    <motion.div
                      layoutId="roleIndicator"
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center"
                      initial={false}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    >
                      <Shield className="w-2 h-2 text-white" />
                    </motion.div>
                  )}
                </motion.button>

                <motion.button
                  type="button"
                  onClick={() => setSelectedRole('requestor')}
                  className={`relative p-2 rounded-lg border transition-all duration-300 ${selectedRole === 'requestor'
                    ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex flex-col items-center space-y-1">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${selectedRole === 'requestor'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-500'
                      }`}>
                      <Users className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-xs font-semibold">Requester</span>
                  </div>
                  {selectedRole === 'requestor' && (
                    <motion.div
                      layoutId="roleIndicator"
                      className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center"
                      initial={false}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    >
                      <Shield className="w-2 h-2 text-white" />
                    </motion.div>
                  )}
                </motion.button>
              </div>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10 pr-3 py-2.5 block w-full border border-gray-300 rounded-xl text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                    placeholder="Enter your email address"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 pr-10 py-2.5 block w-full border border-gray-300 rounded-xl text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="rememberMe"
                    name="rememberMe"
                    type="checkbox"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    className="h-3.5 w-3.5 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <label htmlFor="rememberMe" className="ml-2 block text-xs text-gray-700">
                    Remember me
                  </label>
                </div>
                <div className="text-xs">
                  <Link to="/forgot-password" className="font-semibold text-red-600 hover:text-red-700 transition-colors">
                    Forgot password?
                  </Link>
                </div>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm"
                >
                  {error}
                </motion.div>
              )}

              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={isSubmitting}
                className={`w-full flex justify-center items-center gap-2 py-2.5 px-6 border border-transparent rounded-xl shadow-lg text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${selectedRole === 'requestor'
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:ring-blue-500'
                  : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:ring-red-500'
                  }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Signing in as {selectedRole === 'requestor' ? 'Requester' : 'Donor'}...
                  </>
                ) : (
                  <>
                    Sign in as {selectedRole === 'requestor' ? 'Requester' : 'Donor'}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </motion.button>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-3 bg-white text-gray-500 font-medium">Don't have an account?</span>
                </div>
              </div>

              <div className="mt-4">
                <Link
                  to="/register-donor"
                  className="w-full flex justify-center items-center gap-2 py-2.5 px-6 border-2 border-red-600 rounded-xl shadow-sm text-sm font-semibold text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
                >
                  <Heart className="w-4 h-4" />
                  Create a Donor Account
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  )
}

export default Login